import type { Workflow } from "@/features/workflow/types";
import { fetchClient } from "@/lib/fetchClient";
import type { MutationConfig } from "@/lib/react-query";
import { queryClient } from "@/lib/react-query";
import type { GetFolderResponse } from "@/types/resources";
import { useMutation } from "react-query";
import { isObject } from "../../../utils/typeGuard";

type CloneWorkflowDTO = {
  workflow_id: string;
  workflow_label: string;
};

async function cloneWorkflow({ workflow_id }: CloneWorkflowDTO) {
  await fetchClient.POST("/workflows/{workflow_id}/clone", {
    params: { path: { workflow_id } },
  });
}

type UseCloneWorkflowOptions = {
  config?: MutationConfig<typeof cloneWorkflow>;
};

export const useCloneWorkflow = ({ config }: UseCloneWorkflowOptions = {}) => {
  return useMutation({
    onMutate: async (newWorkflow) => {
      await queryClient.cancelQueries("folders");

      const previousData =
        queryClient.getQueryData<GetFolderResponse>("folders");

      if (previousData) {
        const now = new Date();
        const tempWorkflow: Workflow = {
          id: "temp-" + Date.now(),
          workflow_name: newWorkflow.workflow_label,
          workflow_type: "ocr",
          format_rules: [],
          process_rules: [],
          order: previousData.unassigned_workflows.length,
          folder_id: null,
          created_at: now,
          updated_at: now,
          sorting_executable: false,
        };

        const newData = {
          ...previousData,
          unassigned_workflows: [
            ...previousData.unassigned_workflows,
            tempWorkflow,
          ],
        };
        queryClient.setQueryData("folders", newData);
      }

      return { previousData };
    },
    onError: (_, __, context: unknown) => {
      if (isObject(context) && "previousData" in context) {
        queryClient.setQueryData("folders", context["previousData"]);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries("folders");
    },
    ...config,
    mutationFn: cloneWorkflow,
  });
};
